/**
 * INSTANCE MAPPING FORM
 *
 * Form for IT team to map instances to locations and configure BigQuery.
 */

'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Field,
  FieldLabel,
  FieldSet,
  FieldDescription,
} from '@/components/ui/field';
import { Save, Loader2 } from 'lucide-react';
import type { Task, Location } from '@/types/database';
import { updateLocationStatuses } from '@/lib/onboarding-status';

interface InstanceMappingFormProps {
  task: Task;
  location: Location | null;
  userId: string;
}

export function InstanceMappingForm({
  task,
  location,
  userId,
}: InstanceMappingFormProps) {
  const navigate = useNavigate();
  const supabase = createClient();

  const [openDentalSourceSchema, setOpenDentalSourceSchema] = useState(
    (location as any)?.open_dental_source_schema || ''
  );
  const [openDentalClinic, setOpenDentalClinic] = useState(
    (location as any)?.open_dental_clinic ? String((location as any).open_dental_clinic) : ''
  );
  const [notes, setNotes] = useState('');
  const [savingLoading, setSavingLoading] = useState(false);
  const [completingLoading, setCompletingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isCompleted = task.status === 'COMPLETED';
  const locationInfo = (location as any) || {};
  const clientMeetingStatus = (location as any)?.client_meeting_status || 'PENDING';

  const handleClinicNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow digits
    if (value === '' || /^\d+$/.test(value)) {
      setOpenDentalClinic(value);
    } else {
      // If non-numeric characters were entered, filter them out
      const numericValue = value.replace(/[^\d]/g, '');
      setOpenDentalClinic(numericValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!openDentalSourceSchema.trim()) {
      setError('OpenDental source schema is required');
      return;
    }

    if (!openDentalClinic.trim()) {
      setError('OpenDental clinic is required');
      return;
    }

    const clinicNumber = parseInt(openDentalClinic.trim(), 10);
    if (isNaN(clinicNumber)) {
      setError('OpenDental clinic must be a valid integer');
      return;
    }

    try {
      setSavingLoading(true);
      setError(null);
      setSuccess(false);

      // Update location with OpenDental fields
      const { error: locationError } = await supabase
        .from('locations')
        .update({
          open_dental_source_schema: openDentalSourceSchema.trim(),
          open_dental_clinic: clinicNumber,
        })
        .eq('id', location?.id!);

      if (locationError) throw locationError;

      // Update task status to IN_PROGRESS if it was PENDING
      if (task.status === 'PENDING') {
        const { error: taskUpdateError } = await supabase
          .from('tasks')
          .update({
            status: 'IN_PROGRESS',
            started_at: new Date().toISOString(),
          })
          .eq('id', task.id);

        if (taskUpdateError) throw taskUpdateError;
      }

      // Log activity
      await supabase.rpc('log_activity', {
        p_user_id: userId,
        p_action: 'TASK_UPDATED' as any,
        p_description: `OpenDental configuration updated for ${location?.name}`,
        p_organization_id: task.organization_id,
        p_task_id: task.id,
        p_location_id: location?.id,
      });

      setSuccess(true);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSavingLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!openDentalSourceSchema.trim() || !openDentalClinic.trim()) {
      setError('Please complete all required fields before marking as complete');
      return;
    }

    const clinicNumber = parseInt(openDentalClinic.trim(), 10);
    if (isNaN(clinicNumber)) {
      setError('OpenDental clinic must be a valid integer');
      return;
    }

    try {
      setCompletingLoading(true);
      setError(null);

      // First, ensure location fields are saved
      const { error: locationError } = await supabase
        .from('locations')
        .update({
          open_dental_source_schema: openDentalSourceSchema.trim(),
          open_dental_clinic: clinicNumber,
        })
        .eq('id', location?.id!);

      if (locationError) throw locationError;

      // Mark task as complete
      const { error: taskError } = await supabase
        .from('tasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          completed_by: userId,
          metadata: notes ? { completion_notes: notes } : {},
        })
        .eq('id', task.id);

      if (taskError) throw taskError;

      // Get the current organization status to send to main app
      const { data: org } = await supabase
        .from('organizations')
        .select('status')
        .eq('id', task.organization_id)
        .single();

      // Update this location's status in the main app immediately
      if (location?.id && org?.status) {
        await updateLocationStatuses(
          task.organization_id,
          org.status,
          [{
            id: location.id,
            status: 'AWAITING_DATA_CONFIG',
          }]
        );
      }

      // Check if all IT tasks for this organization are complete
      const { data: orgTasks } = await supabase
        .from('tasks')
        .select('id, status')
        .eq('organization_id', task.organization_id)
        .eq('type', 'IT_PROVISIONING');

      const allComplete = orgTasks?.every(
        (t) => t.id === task.id || t.status === 'COMPLETED'
      ) && org?.status !== 'ACTIVATED';

      // If all IT tasks are complete, update organization status
      if (allComplete) {
        await supabase
          .from('organizations')
          .update({
            status: 'DATA_CONFIGURATION',
          })
          .eq('id', task.organization_id);

        // Update all locations to reflect org status change via edge function
        const { data: locations } = await supabase
          .from('locations')
          .select('id')
          .eq('organization_id', task.organization_id);

        if (locations && locations.length > 0) {
          await updateLocationStatuses(
            task.organization_id,
            'DATA_CONFIGURATION',
            locations.map((loc: any) => ({
              id: loc.id,
              status: 'AWAITING_DATA_CONFIG',
            }))
          );
        }
      }

      // Log activity - wrapped in try-catch so it doesn't block the flow
      try {
        await supabase.rpc('log_activity', {
          p_user_id: userId,
          p_action: 'TASK_COMPLETED',
          p_description: `IT setup task completed for ${location?.name}`,
          p_organization_id: task.organization_id,
          p_task_id: task.id,
          p_location_id: location?.id,
        });

        if (allComplete) {
          await supabase.rpc('log_activity', {
            p_user_id: userId,
            p_action: 'STATUS_CHANGED',
            p_description: 'All IT setup tasks completed, organization status changed to DATA_CONFIGURATION',
            p_organization_id: task.organization_id,
          });
        }
      } catch (logError) {
        console.error('Failed to log activity:', logError);
        // Don't block the flow if logging fails
      }

      navigate('/it-tasks');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete task');
      setCompletingLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FieldSet className="space-y-6">
        {/* Status Message - Locked or Ready */}
        {clientMeetingStatus === 'PENDING' && (
          <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 p-3 rounded-md">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>⚠️ Configuration Locked:</strong> Please complete the client meeting first before configuring settings.
            </p>
          </div>
        )}

        {clientMeetingStatus === 'COMPLETED' && !isCompleted && (
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200">
              <strong>✓ Ready to Configure:</strong> You can now proceed with the OpenDental configuration.
            </p>
          </div>
        )}

        {/* Location Information - Read-only fields */}
        {(locationInfo.source_schema && locationInfo.source_schema !== 'na') ||
        (locationInfo.source_name && locationInfo.source_name !== 'na') ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Location Information
            </h3>
            <div className="space-y-3 bg-gray-50 dark:bg-gray-900/30 p-4 rounded-lg">
              {locationInfo.source_schema && locationInfo.source_schema !== 'na' && (
                <div>
                  <p className="text-xs text-muted-foreground">Source Schema</p>
                  <p className="font-mono text-sm text-gray-700 dark:text-gray-300">
                    {locationInfo.source_schema}
                  </p>
                </div>
              )}

              {locationInfo.source_name && locationInfo.source_name !== 'na' && (
                <div>
                  <p className="text-xs text-muted-foreground">Source Name</p>
                  <p className="font-mono text-sm text-gray-700 dark:text-gray-300">
                    {locationInfo.source_name}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* OpenDental Configuration Section */}
        <div>
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-3">
            OpenDental Configuration
          </h3>
          <div className="space-y-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              {/* OpenDental Source Schema */}
              <Field>
                <FieldLabel>OpenDental Source Schema *</FieldLabel>
                <Input
                  value={openDentalSourceSchema}
                  onChange={(e) => setOpenDentalSourceSchema(e.target.value)}
                  placeholder="e.g., opendental_schema_001"
                  disabled={savingLoading || completingLoading || clientMeetingStatus === 'PENDING'}
                  required
                />
                <FieldDescription>
                  Reference to the OpenDental source schema for this location
                </FieldDescription>
              </Field>

              {/* OpenDental Clinic */}
              <Field>
                <FieldLabel>OpenDental Clinic *</FieldLabel>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={openDentalClinic}
                  onChange={handleClinicNumberChange}
                  placeholder="e.g., 123"
                  disabled={savingLoading || completingLoading || clientMeetingStatus === 'PENDING'}
                  required
                />
                <FieldDescription>
                  OpenDental clinic identifier (numeric only) for this location
                </FieldDescription>
              </Field>
          </div>
        </div>

        {/* Notes (only shown when completing) */}
        {!isCompleted && openDentalSourceSchema && openDentalClinic && (
          <Field>
            <FieldLabel>Completion Notes (Optional)</FieldLabel>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this configuration..."
              disabled={savingLoading || completingLoading || clientMeetingStatus === 'PENDING'}
            />
            <FieldDescription>
              Notes will be saved with the task completion record
            </FieldDescription>
          </Field>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-3 rounded-md">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 p-3 rounded-md">
            Configuration saved successfully!
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={savingLoading || completingLoading || isCompleted || clientMeetingStatus === 'PENDING'}
            variant="outline"
          >
            {savingLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Configuration
              </>
            )}
          </Button>

          {!isCompleted && (
            <Button
              type="button"
              onClick={handleComplete}
              disabled={savingLoading || completingLoading || !openDentalSourceSchema || !openDentalClinic || clientMeetingStatus === 'PENDING'}
              className="flex-1"
            >
              {completingLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing...
                </>
              ) : (
                'Complete Task'
              )}
            </Button>
          )}

          {isCompleted && (
            <div className="flex-1 flex items-center justify-center text-sm text-green-600 dark:text-green-400">
              ✓ Task Completed
            </div>
          )}
        </div>
      </FieldSet>
    </form>
  );
}
